import { FC, useEffect, useRef } from 'react';
import { ScanModel } from '../network';
import { useAuthContext } from '../../auth';
import {
  CompareModeEnum,
  useCompareStore,
} from './CompareControls/useCompareStore';
import { CanvasView } from './CanvasView/CanvasView';
import { CanvasControls } from './CanvasControls/CanvasControls';
import { useMaxSizeForViewport } from './useWindowDimensions';
import { ScanCompareSelector } from './CompareControls/ScanCompareSelector';
import { Loader } from '../../ui';
import { Alert } from '@mui/material';
import { SideControls } from './SideControls';

interface MeshViewerV2Props {
  scans: ScanModel[];
}

export const MeshViewerV2: FC<MeshViewerV2Props> = ({ scans }) => {
  const { authToken } = useAuthContext();
  const {
    compareMode,
    leftScan,
    rightScan,
    error,
    setIsScanSelectorOpen,
    isLoading,
  } = useCompareStore();

  const canvasContainerRef = useRef<HTMLDivElement>(null);
  useMaxSizeForViewport(canvasContainerRef, 24);

  useEffect(() => {
    if (compareMode === CompareModeEnum.Dual) {
      if (!rightScan) {
        setIsScanSelectorOpen('secondary');
      }
    }
  }, [compareMode, rightScan, setIsScanSelectorOpen]);

  if (isLoading) {
    return (
      <DynamicMeshViewerContainer containerRef={canvasContainerRef}>
        <Loader />
      </DynamicMeshViewerContainer>
    );
  }

  if (error) {
    return <Alert severity='error'>{error.message}</Alert>;
  }

  if (!leftScan) {
    return <Alert severity='error'>No scan selected</Alert>;
  }
  if (!authToken) {
    return <Alert severity='error'>No auth token</Alert>;
  }

  const isDual = compareMode === CompareModeEnum.Dual;

  return (
    <DynamicMeshViewerContainer containerRef={canvasContainerRef}>
      <CanvasView scan={leftScan} accessToken={authToken} sourceId='primary' />
      {isDual && rightScan && (
        <CanvasView
          scan={rightScan}
          accessToken={authToken}
          sourceId='secondary'
        />
      )}

      <CanvasControls />
      <ScanCompareSelector scans={scans} containerRef={canvasContainerRef} />
      <SideControls fullScreenContainerRef={canvasContainerRef} />
    </DynamicMeshViewerContainer>
  );
};

interface DynamicMeshViewerContainerProps {
  containerRef: React.RefObject<HTMLDivElement>;
  children: React.ReactNode;
}

const DynamicMeshViewerContainer: FC<DynamicMeshViewerContainerProps> = ({
  containerRef,
  children,
}) => {
  return (
    <div
      ref={containerRef}
      style={{
        marginTop: 10,
        display: 'flex',
        flexDirection: 'row',
        gap: '8px',
        position: 'relative',
        height: 400, // fallback height
      }}
    >
      {children}
    </div>
  );
};
